import { Component, type ErrorInfo, type ReactNode } from "react"
import { useLocation } from "react-router-dom"

import { Button } from "@/components/ui/controls/button"

type BoundaryProps = { children: ReactNode }
type BoundaryState = { hasError: boolean }

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false }

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error:", error, info.componentStack)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex min-h-[60dvh] w-full flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          This page couldn&apos;t be displayed. Try reloading, or go back and open it again.
        </p>
        <Button onClick={() => window.location.reload()}>Reload page</Button>
      </div>
    )
  }
}

/**
 * Catches render errors so a crash on one page shows a fallback instead of a white screen.
 * Keyed on the path so navigating to another page clears the error.
 */
export function RouteErrorBoundary({ children }: BoundaryProps) {
  const { pathname } = useLocation()
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
}
